<template>
  <div class="dashboard-wrapper">
    <div class="main-container">
      <el-row :gutter="20" class="stat-container">
        <el-col :span="6" v-for="(item, index) in topMetrics" :key="index">
          <el-card shadow="hover" :class="['metric-card', { 'is-active': activeMetric === item.key }]"
            @click.native="handleMetricChange(item)">
            <div class="metric-content">
              <div class="metric-info">
                <p class="label">{{ item.label }}</p>
                <h2 class="count">{{ item.value }}</h2>
                <p class="trend">
                  <span class="up"><i class="el-icon-caret-top"></i> 12%</span>
                  较上月
                </p>
              </div>
              <div class="metric-icon" :style="{ background: item.color + '15', color: item.color }">
                <i :class="item.icon"></i>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
      <el-row :gutter="20">
        <el-card shadow="never" class="chart-card">
          <div slot="header" class="card-header">
            <span class="title">数据趋势分析</span>
            <div class="header-ops">
              <el-radio-group v-model="timeRange" size="mini">
                <el-radio-button label="7">近7天</el-radio-button>
                <el-radio-button label="30">近30天</el-radio-button>
              </el-radio-group>
            </div>
          </div>
          <div id="mainChart" class="chart-box"></div>
        </el-card>
      </el-row>
    </div>
  </div>
</template>

<script>
// 示例：只引入折线图必需的模块
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import { GridComponent, TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([LineChart, GridComponent, TooltipComponent, CanvasRenderer]);
export default {
  name: 'RecruitmentDashboard',
  data() {
    return {
      activeMetric: 'resume', // 默认选中项
      timeRange: '7',
      chartInstance: null,
      // 模拟各维度的 BI 数据
      mockData: {
        resume: [120, 210, 150, 280, 210, 390, 420],
        message: [50, 80, 120, 90, 150, 180, 210],
        active: [10, 30, 25, 45, 30, 55, 60],
        source: [5, 12, 8, 15, 10, 22, 18]
      },
      topMetrics: [
        { label: '今日招呼', value: '200', key: 'greet', icon: 'el-icon-mouse', color: '#409EFF' },
        { label: '今日简历', value: '1,284', key: 'resume', icon: 'el-icon-document', color: '#409EFF' },
        { label: '今日沟通', value: '856', key: 'message', icon: 'el-icon-chat-line-round', color: '#67C23A' },
        { label: '今日复聊', value: '124', key: 'rechat', icon: 'el-icon-connection', color: '#67C23A' },
        { label: '今日收藏', value: '124', key: 'collection', icon: 'el-icon-collection', color: '#E6A23C' },
        { label: '今日面试邀约', value: '124', key: 'invitation', icon: 'el-icon-link', color: '#E6A23C' },
        { label: '今日交换电话', value: '45', key: 'phone', icon: 'el-icon-mobile', color: '#F56C6C' },
        { label: '今日交换微信', value: '45', key: 'wechat', icon: 'el-icon-chat-dot-round', color: '#F56C6C' }
      ]
    };
  },
  mounted() {
    this.initChart();
    // 监听窗口缩放，防止图表变形
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    initChart() {
      const chartDom = document.getElementById('mainChart');
      this.chartInstance = echarts.init(chartDom);
      this.renderChart();
    },

    handleMetricChange(item) {
      this.activeMetric = item.key;
      this.renderChart();
    },

    renderChart() {
      const currentItem = this.topMetrics.find(m => m.key === this.activeMetric);
      const dataPoints = this.mockData[this.activeMetric];

      const option = {
        tooltip: {
          trigger: 'axis',
          backgroundColor: '#fff',
          textStyle: { color: '#666' },
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          borderWidth: 0
        },
        grid: {
          top: '15%', left: '3%', right: '3%', bottom: '5%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: ['周一', '周二', '周三', '周四', '周五', '周六', '周日'],
          axisLine: { lineStyle: { color: '#f0f0f0' } },
          axisLabel: { color: '#999' }
        },
        yAxis: {
          type: 'value',
          splitLine: { lineStyle: { type: 'dashed', color: '#f5f5f5' } }
        },
        series: [{
          name: currentItem.label,
          type: 'line',
          smooth: true, // 曲线平滑
          showSymbol: false,
          lineStyle: { width: 4, color: currentItem.color },
          areaStyle: {
            // 设置渐变色填充
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: `${currentItem.color}44` },
              { offset: 1, color: `${currentItem.color}00` }
            ])
          },
          data: dataPoints
        }]
      };

      this.chartInstance.setOption(option, true);
    },

    handleResize() {
      this.chartInstance && this.chartInstance.resize();
    }
  }
};
</script>

<style lang="scss" scoped>
* {
  box-sizing: border-box;
}

.dashboard-wrapper {
  padding: 24px;
  height: calc(100vh - 50px);
  padding: 20px 0 0 0;
  display: flex;
  justify-content: center;

  .main-container {
    width: 1200px;
  }

  .stat-container {
    margin-bottom: 24px;

    ::v-deep .el-col-6 {
      margin-bottom: 10px;
    }
  }

  // 指标卡片美化
  .metric-card {
    border-radius: 12px;
    border: 1px solid transparent;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;



    .metric-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;
    }



    .label {
      font-size: 14px;
      color: #909399;
      margin: 0;
    }

    .count {
      font-size: 28px;
      font-weight: bold;
      color: #303133;
      margin: 8px 0;
    }

    .trend {
      font-size: 12px;
      color: #999;
      margin: 0;

      .up {
        color: #f56c6c;
        margin-right: 4px;
        font-weight: bold;
      }
    }

    .metric-icon {
      width: 54px;
      height: 54px;
      border-radius: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 26px;
    }

    // 激活状态样式
    &.is-active {
      border-color: #409eff;
      background-color: #f0f7ff;
      transform: translateY(-5px);
      box-shadow: 0 10px 20px rgba(64, 158, 255, 0.1);

      .count {
        color: #409eff;
      }
    }
  }

  .chart-card {
    border-radius: 16px;
    border: none;

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .title {
        font-weight: bold;
        color: #333;
      }
    }

    .chart-box {
      height: 450px;
      width: 100%;
    }
  }

  // 会员卡片：黑金风格
  .premium-card {
    background: linear-gradient(135deg, #2b2b2b 0%, #111111 100%);
    border-radius: 16px;
    padding: 24px;
    color: #fff;
    text-align: center;
    position: relative;
    overflow: hidden;
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.2);

    .badge {
      position: absolute;
      top: 10px;
      right: 10px;
      background: rgba(242, 201, 76, 0.2);
      color: #f2c94c;
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 10px;
      user-select: none;
    }

    .crown {
      font-size: 36px;
      color: #f2c94c;
      margin-bottom: 15px;
    }

    h3 {
      margin: 10px 0;
      color: #f2c94c;
      font-size: 20px;
      user-select: none;
    }

    p {
      font-size: 12px;
      color: #999;
      line-height: 1.6;
      margin-bottom: 20px;
    }

    .renew-btn {
      width: 100%;
      background: #f2c94c;
      border: none;
      font-weight: bold;
      color: #333;

      &:hover {
        background: #e5bc41;
        transform: scale(1.02);
      }
    }

    .quick-tools {
      display: flex;
      justify-content: space-between;
      margin-top: 25px;

      .tool-item {
        flex: 1;
        font-size: 12px;

        i {
          display: block;
          font-size: 20px;
          color: #f2c94c;
          margin-bottom: 8px;
        }
      }
    }
  }


}
</style>